import React, { useState, useEffect, useContext } from "react";
import { UpdateProfile, UploadProfileImage } from "../../api-requests/Updates";
import { GetProfile, VerifyToken } from "../../api-requests/Get";
import { getBearerHeaders } from "../../helpers/GetBearerHeaders";
import { UserContext } from "../../context/UserContext";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import {
  loadRecaptchaScript,
  unloadRecaptchaScript,
} from "../../helpers/Recapture";
import { config } from "../../config/config";
import {
  UserIcon,
  PhotoIcon,
  PaintBrushIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";

declare const grecaptcha: any;

interface ProfileData {
  id: string;
  username: string;
  display_name: string;
  generation: number;
  bg_color: string;
  profileImg?: string;
}

const generations = [
  "Silent Generation (1928-1945)",
  "Baby Boomers (1946-1964)",
  "Generation X (1965-1980)",
  "Millennials (1981-1996)",
  "Generation Z (1997-2012)",
];

const ProfileSettings: React.FC = () => {
  const { user } = useContext(UserContext);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Form states
  const [displayName, setDisplayName] = useState("");
  const [generation, setGeneration] = useState(1);
  const [profileImg, setProfileImg] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bgColor, setBgColor] = useState("#3B82F6");

  const siteKey = config.RECAPTURE_SITE_KEY;

  useEffect(() => {
    if (siteKey) {
      loadRecaptchaScript(siteKey)
        .then(() => {
          console.log("reCAPTCHA script loaded for profile settings");
        })
        .catch((err) => {
          console.error("Failed to load reCAPTCHA script:", err);
        });
    }

    return () => {
      unloadRecaptchaScript();
    };
  }, [siteKey]);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const headers = getBearerHeaders();
      const response = await GetProfile(headers);

      if (response.success) {
        setProfile(response.user);
        setDisplayName(response.user.display_name);
        setGeneration(response.user.generation || 1);
        setProfileImg(response.user.profileImg || "");
        setBgColor(response.user.bg_color || "#3B82F6");
      }
    } catch (err) {
      setError("Failed to load profile");
      console.error("Error loading profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    setUploadingImage(true);
    setError("");

    try {
      const headers = getBearerHeaders();
      const response = await UploadProfileImage(file, headers);

      if (response.success) {
        setProfileImg(response.imageUrl);
        setMessage("Profile image uploaded successfully!");
        // Optionally update the profile state
        if (profile) {
          setProfile({ ...profile, profileImg: response.imageUrl });
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");

    try {
      // reCAPTCHA verification
      if (window.grecaptcha && siteKey) {
        const token = await window.grecaptcha.execute(siteKey);
        const isValid = await VerifyToken(token);
        if (!isValid) {
          setError("Security verification failed. Please try again.");
          setSaving(false);
          return;
        }
      } else {
        console.warn("reCAPTCHA not available for profile update");
      }

      const headers = getBearerHeaders();
      const updateData = {
        display_name: displayName,
        generation: generation,
        profileImg: profileImg || null,
        bg_color: bgColor,
      };

      const response = await UpdateProfile(updateData, headers);

      if (response.success) {
        setMessage("Profile updated successfully!");
        setProfile(response.user);
        // Optionally update the user context here if needed
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user?.isloggedin) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-400">Please log in to edit your profile.</p>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <UserIcon className="w-6 h-6" />
            Profile Settings
          </h2>

          {message && (
            <div className="mb-4 p-3 bg-green-800 border border-green-600 rounded-md">
              <p className="text-green-100">{message}</p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-800 border border-red-600 rounded-md">
              <p className="text-red-100">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email (non-editable) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email (Login)
              </label>
              <input
                type="email"
                value={profile?.username || ""}
                disabled
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email cannot be changed
              </p>
            </div>

            {/* Display Name */}
            <div>
              <label className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <UserIcon className="w-4 h-4" />
                Display Name (@username)
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your display name"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will be your @username in URLs
              </p>
            </div>

            {/* Generation */}
            <div>
              <label className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <CalendarDaysIcon className="w-4 h-4" />
                Generation
              </label>
              <select
                value={generation}
                onChange={(e) => setGeneration(parseInt(e.target.value))}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {generations.map((gen, index) => (
                  <option key={index + 1} value={index + 1}>
                    {gen}
                  </option>
                ))}
              </select>
            </div>

            {/* Profile Image Upload */}
            <div>
              <label className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <PhotoIcon className="w-4 h-4" />
                Profile Image
              </label>

              {/* Current image preview */}
              {profileImg && (
                <div className="mb-3 flex items-center gap-3">
                  <img
                    src={profileImg}
                    alt="Current profile"
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-600"
                  />
                  <span className="text-sm text-gray-400">Current image</span>
                </div>
              )}

              {/* File input */}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={uploadingImage}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 disabled:opacity-50"
              />

              {uploadingImage && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  <span className="text-sm text-blue-400">
                    Uploading image...
                  </span>
                </div>
              )}

              <p className="text-xs text-gray-500 mt-1">
                Upload a profile image (max 5MB). Supported formats: JPG, PNG,
                GIF, WebP
              </p>
            </div>

            {/* Background Color */}
            <div>
              <label className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <PaintBrushIcon className="w-4 h-4" />
                Profile Background Color
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="w-12 h-12 rounded-md border border-gray-600 bg-gray-700"
                />
                <input
                  type="text"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="flex-1 p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#3B82F6"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-blue-600 text-white p-3 rounded-md font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>

            {/* reCAPTCHA container */}
            <div id="recaptcha-container" style={{ display: "none" }}></div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ProfileSettings;
